import * as React from 'react'
import { X, ChevronLeft } from 'lucide-react'
import { cn } from '@ui/lib/utils'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  //TODO: handle blur/unblur globally in the scope of the https://wellesley.youtrack.cloud/issue/WLSY-2309/Modal-support
  React.useEffect(() => {
    const bluredElements = document.querySelectorAll('.blured-modal-overlay')

    bluredElements.forEach(
      (el) => el && ((el as HTMLElement).style.filter = 'blur(5px)'),
    )

    return () => {
      const openedDialogsCount = Array.from(
        document.querySelectorAll(
          '[role="dialog"]:not([data-slot="popover-content"])',
        ),
      ).length

      if (openedDialogsCount === 0) {
        bluredElements.forEach(
          (el) => el && ((el as HTMLElement).style.filter = 'none'),
        )
      }
    }
  }, [])

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-testid="dialog_overlay"
      className={cn(
        'fixed inset-0 z-dialog bg-black opacity-[0.8] data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  )
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const closeButtonVariants = cva('', {
  variants: {
    closeVariant: {
      none: '',
      insideCard:
        'absolute right-m top-m rounded-xs opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-gray-100 data-[state=open]:text-gray-500 text-gray-400',
      outsideCard: 'fixed right-[30px] top-[30px] z-100 text-gray-400',
      mobileGallery: 'fixed right-m top-[62px] text-white z-[110]',
    },
  },
  defaultVariants: {
    closeVariant: 'outsideCard',
  },
})

const closeButtonSizes = cva('', {
  variants: {
    closeSize: {
      small: 'size-6',
      big: 'size-[42px]',
      '18x18': 'size-[18px]',
    },
  },
  defaultVariants: {
    closeSize: 'small',
  },
})

export type TDialogContent = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> &
  VariantProps<typeof closeButtonSizes> &
  VariantProps<typeof closeButtonVariants> & {
    title?: string
    titleIcon?: React.ReactNode
    showClose?: boolean
    dialogName?: string
    overlayClassName?: string
    titleClassName?: string
    childrenClassName?: string
    footerClassName?: string
    onBack?: () => void
    onCloseModal?: () => void
    renderFooter?: () => JSX.Element | null
    closeOnOutsideClick?: boolean
  }

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  TDialogContent
>(
  (
    {
      title,
      titleIcon,
      className,
      dialogName,
      showClose = true,
      closeVariant = 'outsideCard',
      closeSize = 'big',
      children,
      titleClassName,
      overlayClassName,
      childrenClassName,
      footerClassName,
      onBack,
      onCloseModal,
      renderFooter,
      onScroll,
      closeOnOutsideClick = true,
      ...props
    },
    ref,
  ) => {
    const CloseButton = (
      <DialogPrimitive.Close
        className={cn(closeButtonVariants({ closeVariant }))}
      >
        <X
          onClick={onCloseModal}
          fill="currentColor"
          className={cn(closeButtonSizes({ closeSize }))}
        />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    )

    // todo: return if needed data-[state=open]:animate-in data-[state=closed]:animate-out

    return (
      <DialogPortal>
        <DialogOverlay
          className={overlayClassName}
          onClick={closeOnOutsideClick ? onCloseModal : undefined}
        />
        {showClose && closeVariant !== 'insideCard' && CloseButton}
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed left-[50%] top-[50%] z-[101] grid max-w-[600px] translate-x-[-50%] translate-y-[-50%] !overflow-visible rounded-sm bg-white shadow-lg outline-none duration-200 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] max-md:w-[calc(100vw-32px)] md:w-full [&>div]:min-w-0',
            className,
          )}
          {...props}
        >
          {title && (
            <h3
              className={cn(
                'flex items-center justify-center gap-xxs border-b border-gray-200 p-m text-center text-lg font-semibold text-gray-700',
                titleClassName,
              )}
            >
              {titleIcon}
              {title}
            </h3>
          )}
          <div
            id={dialogName || 'dialog-content'}
            className={cn('p-m', childrenClassName)}
            onScroll={onScroll}
          >
            {children}
          </div>
          {showClose && closeVariant === 'insideCard' && CloseButton}
          {onBack && (
            <button
              data-testid="dialog-back-button"
              aria-label="Back"
              onClick={onBack}
              className="absolute left-m top-m"
            >
              <ChevronLeft color="var(--gray-400)" size={24} />
            </button>
          )}
          <DialogFooter className={cn('empty:hidden', footerClassName)}>
            {renderFooter?.()}
          </DialogFooter>
        </DialogPrimitive.Content>
      </DialogPortal>
    )
  },
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'border-b border-gray-200 p-m text-center text-lg font-semibold text-gray-700',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex border-t border-gray-200 p-m', className)}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-gray-500', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
